/**
 * GoodGuardian — Child Support Calculator
 *
 * State-specific estimate using:
 *   - Income Shares Model  (majority of states)
 *   - Percentage of Income Model (AK, MS, ND, NV, TX, WI)
 *
 * Output is an approximation. Exact amounts require the state's
 * official worksheet. Results feed directly into the Document Drafter.
 */
import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { useApp } from '@/context/AppContext'

const LS_KEY = 'cg_cs_calc'

function loadSaved() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

// ── State formula database ────────────────────────────────────────────────────

/** Percentage-of-income states: pcts = [1 child, 2, 3, 4, 5+ children] */
const PCT_STATES = {
  AK: { label: 'Percentage of Income (gross)', pcts: [20, 27, 33, 37, 41], net: false },
  MS: { label: 'Percentage of Income (gross)', pcts: [14, 20, 22, 24, 26], net: false },
  ND: { label: 'Percentage of Income (net)',   pcts: [14, 19, 22, 25, 29], net: true  },
  NV: { label: 'Percentage of Income (gross)', pcts: [18, 25, 29, 31, 34], net: false },
  TX: { label: 'Percentage of Income (net)',   pcts: [20, 25, 30, 35, 40], net: true  },
  WI: { label: 'Percentage of Income (gross)', pcts: [17, 25, 29, 31, 34], net: false },
}

/**
 * Income Shares: approximate BCSO as % of combined gross.
 * Source: OCSE national average schedule, 2023 update.
 */
const IS_RATES = [0.17, 0.25, 0.29, 0.31, 0.35] // index = numChildren - 1

const ALL_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado',
  'Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho',
  'Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana',
  'Maine','Maryland','Massachusetts','Michigan','Minnesota',
  'Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York',
  'North Carolina','North Dakota','Ohio','Oklahoma','Oregon',
  'Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming',
]

const STATE_ABBRS = {
  Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR',
  California:'CA', Colorado:'CO', Connecticut:'CT', Delaware:'DE',
  Florida:'FL', Georgia:'GA', Hawaii:'HI', Idaho:'ID',
  Illinois:'IL', Indiana:'IN', Iowa:'IA', Kansas:'KS',
  Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
  Massachusetts:'MA', Michigan:'MI', Minnesota:'MN',
  Mississippi:'MS', Missouri:'MO', Montana:'MT', Nebraska:'NE',
  Nevada:'NV', 'New Hampshire':'NH', 'New Jersey':'NJ',
  'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC',
  'North Dakota':'ND', Ohio:'OH', Oklahoma:'OK', Oregon:'OR',
  Pennsylvania:'PA', 'Rhode Island':'RI', 'South Carolina':'SC',
  'South Dakota':'SD', Tennessee:'TN', Texas:'TX', Utah:'UT',
  Vermont:'VT', Virginia:'VA', Washington:'WA',
  'West Virginia':'WV', Wisconsin:'WI', Wyoming:'WY',
}

const STATE_NOTES = {
  CA: 'California uses the Dissomaster computer program with many additional factors. This is an approximation.',
  NY: 'New York caps the guideline income at ~$163,000 combined (2024). Above that, the court has discretion.',
  DE: 'Delaware uses the Melson Formula, which first satisfies each parent\'s basic needs. This is an approximation.',
  HI: 'Hawaii uses the Melson Formula. This is an approximation.',
  MT: 'Montana uses the Melson Formula. This is an approximation.',
  MA: 'Massachusetts uses a single-income model in some situations. Verify with the MA Child Support Guidelines.',
  IL: 'Illinois uses a net income model. If entering gross income, reduce by ~20–25% for taxes.',
  MN: 'Minnesota uses Parental Income for Determining Child Support (PICS). This is an approximation.',
}

// ── Formula engine ────────────────────────────────────────────────────────────
function calculate({ state, numChildren, myMonthly, partnerMonthly, myTimePct, healthcare, childcare }) {
  if (!myMonthly || myMonthly <= 0) return null

  const abbr    = STATE_ABBRS[state] || (state?.length === 2 ? state.toUpperCase() : null)
  const n       = Math.min(Math.max(1, numChildren), 5)
  const idx     = n - 1
  const hcShare = (healthcare || 0) / 2             // split evenly
  const ccShare = (childcare  || 0) * 0.75          // 75% work-related portion

  const pctModel = abbr && PCT_STATES[abbr]

  if (pctModel) {
    // ── Percentage of Income model ───────────────────────────────────────────
    const pct         = pctModel.pcts[idx] / 100
    const adj         = pctModel.net ? 0.8 : 1       // rough net conversion
    const myAdj       = myMonthly * adj
    const partnerAdj  = (partnerMonthly || 0) * adj

    const iAmPaying     = myTimePct <= 50
    const payerAdj      = iAmPaying ? myAdj : partnerAdj
    const baseObligation = payerAdj * pct
    const amount        = Math.round(baseObligation + hcShare + ccShare)

    return {
      amount,
      iAmPaying,
      model: pctModel.label,
      abbr,
      note: STATE_NOTES[abbr] || null,
      breakdown: [
        {
          label: `${iAmPaying ? 'Your' : "Co-parent's"} ${pctModel.net ? 'est. net' : 'gross'} monthly income`,
          value: `$${Math.round(iAmPaying ? myMonthly : (partnerMonthly || 0)).toLocaleString()}`,
        },
        {
          label: `${pctModel.pcts[idx]}% guideline (${n} child${n > 1 ? 'ren' : ''})`,
          value: `$${Math.round(baseObligation).toLocaleString()}`,
        },
        ...(healthcare > 0 ? [{ label: 'Healthcare (50% share)',     value: `+$${Math.round(hcShare).toLocaleString()}` }] : []),
        ...(childcare  > 0 ? [{ label: 'Childcare (75% work-related)', value: `+$${Math.round(ccShare).toLocaleString()}` }] : []),
      ],
    }
  }

  // ── Income Shares model ─────────────────────────────────────────────────────
  const combined = myMonthly + (partnerMonthly || 0)
  if (combined <= 0) return null

  const bcso           = combined * IS_RATES[idx]
  const myFraction     = myMonthly / combined
  const partnerFraction = (partnerMonthly || 0) / combined
  const iAmPaying      = myTimePct <= 50

  let base
  if (iAmPaying) {
    const raw    = myFraction * bcso
    const credit = myTimePct > 30 ? ((myTimePct - 30) / 20) * 0.5 * raw : 0
    base = raw - credit
  } else {
    const partnerTime = 100 - myTimePct
    const raw         = partnerFraction * bcso
    const credit      = partnerTime > 30 ? ((partnerTime - 30) / 20) * 0.5 * raw : 0
    base = raw - credit
  }

  const amount = Math.round(base + hcShare + ccShare)

  return {
    amount,
    iAmPaying,
    model: 'Income Shares',
    abbr,
    note: STATE_NOTES[abbr] || null,
    breakdown: [
      { label: 'Combined gross monthly income',                              value: `$${Math.round(combined).toLocaleString()}` },
      { label: `Basic Child Support Obligation (${(IS_RATES[idx] * 100).toFixed(0)}%, ${n} child${n > 1 ? 'ren' : ''})`, value: `$${Math.round(bcso).toLocaleString()}` },
      { label: `${iAmPaying ? 'Your' : "Co-parent's"} proportional share`,  value: `$${Math.round(iAmPaying ? myFraction * bcso : partnerFraction * bcso).toLocaleString()}` },
      ...(myTimePct > 30 && iAmPaying   ? [{ label: `Parenting time credit (${myTimePct}% time)`, value: `-$${Math.round(myFraction * bcso - base).toLocaleString()}` }] : []),
      ...(healthcare > 0 ? [{ label: 'Healthcare (50% share)',               value: `+$${Math.round(hcShare).toLocaleString()}` }] : []),
      ...(childcare  > 0 ? [{ label: 'Childcare (75% work-related)',         value: `+$${Math.round(ccShare).toLocaleString()}` }] : []),
    ],
  }
}

// ── Money input ───────────────────────────────────────────────────────────────
function MoneyInput({ label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">{label}</label>
      {hint && <p className="text-xs text-slate-400 mb-1.5">{hint}</p>}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm pointer-events-none">$</span>
        <input
          type="number" inputMode="decimal" min="0"
          value={value === 0 ? '' : value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          placeholder={placeholder || '0'}
          className="input-base pl-7 text-sm"
        />
      </div>
    </div>
  )
}

// ── Screen ────────────────────────────────────────────────────────────────────
export default function ChildSupport() {
  const { user } = useApp()
  const navigate = useNavigate()

  // Pull parenting time % from ParentingTime screen if saved
  const savedTimePct = useMemo(() => {
    try {
      const pt = JSON.parse(localStorage.getItem('cg_parenting_time') || 'null')
      return pt?.orderedPct ?? 50
    } catch { return 50 }
  }, [])

  const [form, setForm] = useState(() => {
    const saved = loadSaved()
    return saved || {
      state:          user?.state || '',
      numChildren:    1,
      myMonthly:      0,
      partnerMonthly: 0,
      myTimePct:      savedTimePct,
      healthcare:     0,
      childcare:      0,
    }
  })

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(form))
  }, [form])

  const result = useMemo(() => calculate(form), [form])

  const abbr    = form.state ? STATE_ABBRS[form.state] : null
  const pctInfo = abbr ? PCT_STATES[abbr] : null

  const summaryText = result ? [
    `CHILD SUPPORT ESTIMATE — ${form.state || 'Unknown State'}`,
    `Model: ${result.model}`,
    `Children: ${form.numChildren}`,
    `My gross monthly income: $${form.myMonthly.toLocaleString()}`,
    form.partnerMonthly ? `Co-parent gross monthly income: $${form.partnerMonthly.toLocaleString()}` : '',
    `My parenting time: ${form.myTimePct}%`,
    form.healthcare ? `Monthly healthcare (children): $${form.healthcare.toLocaleString()}` : '',
    form.childcare  ? `Monthly childcare costs: $${form.childcare.toLocaleString()}`         : '',
    ``,
    `ESTIMATED MONTHLY SUPPORT: $${result.amount.toLocaleString()} (${result.iAmPaying ? 'I pay co-parent' : 'Co-parent pays me'})`,
    `Likely range: $${Math.round(result.amount * 0.85).toLocaleString()} – $${Math.round(result.amount * 1.15).toLocaleString()}/mo`,
    ``,
    `NOTE: This is an estimate only. Use the official ${form.state || 'state'} Child Support Worksheet for exact figures. Attorney review recommended.`,
  ].filter(l => l !== undefined).join('\n') : ''

  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    await navigator.clipboard.writeText(summaryText).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDraft = () => {
    navigate('/documents', { state: { autoTemplate: 'child-support', prefillContext: summaryText } })
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Child Support Calculator" showBack />

      <div className="screen no-scrollbar px-4 py-5 flex flex-col gap-5">

        {/* ── State & Setup ─────────────────────────────────────────────────── */}
        <section className="card flex flex-col gap-4">
          <h2 className="font-bold text-slate-800 dark:text-slate-100 text-sm uppercase tracking-wide">State &amp; Setup</h2>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">State</label>
            <select value={form.state} onChange={e => set('state', e.target.value)}
              className="input-base text-sm">
              <option value="">Select your state…</option>
              {ALL_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Formula badge */}
          {form.state && (
            <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <span className="text-sm">📐</span>
              <p className="text-xs text-brand-700 dark:text-brand-300 font-medium">
                {form.state} uses the <strong>{pctInfo ? pctInfo.label : 'Income Shares'}</strong> model
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">
              Number of children
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => set('numChildren', n)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${
                    form.numChildren === n
                      ? 'bg-brand-700 text-white border-brand-700'
                      : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200'
                  }`}>
                  {n === 5 ? '5+' : n}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── Income ────────────────────────────────────────────────────────── */}
        <section className="card flex flex-col gap-4">
          <h2 className="font-bold text-slate-800 dark:text-slate-100 text-sm uppercase tracking-wide">Monthly Gross Income</h2>
          <MoneyInput
            label="Your gross monthly income"
            hint="Before taxes — include wages, self-employment, bonuses, commissions"
            value={form.myMonthly}
            onChange={v => set('myMonthly', v)}
            placeholder="e.g. 4500"
          />
          <MoneyInput
            label="Co-parent's gross monthly income"
            hint={pctInfo ? 'Used for healthcare/childcare split' : 'Required for Income Shares calculation'}
            value={form.partnerMonthly}
            onChange={v => set('partnerMonthly', v)}
            placeholder="e.g. 3200"
          />
        </section>

        {/* ── Adjustments ───────────────────────────────────────────────────── */}
        <section className="card flex flex-col gap-4">
          <h2 className="font-bold text-slate-800 dark:text-slate-100 text-sm uppercase tracking-wide">Adjustments</h2>

          <div>
            <div className="flex justify-between items-baseline mb-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Your parenting time
              </label>
              <span className="text-sm font-bold text-brand-700">{form.myTimePct}%</span>
            </div>
            <input type="range" min="0" max="100" step="1"
              value={form.myTimePct}
              onChange={e => set('myTimePct', parseInt(e.target.value))}
              className="w-full accent-brand-600" />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>0%</span><span>50/50</span><span>100%</span>
            </div>
          </div>

          <MoneyInput
            label="Monthly healthcare premium (children only)"
            hint="The portion of your health insurance covering the children"
            value={form.healthcare}
            onChange={v => set('healthcare', v)}
            placeholder="0"
          />
          <MoneyInput
            label="Monthly work-related childcare costs"
            hint="Daycare, after-school programs needed for employment"
            value={form.childcare}
            onChange={v => set('childcare', v)}
            placeholder="0"
          />
        </section>

        {/* ── Result ────────────────────────────────────────────────────────── */}
        {result ? (
          <>
            <section className={`rounded-2xl border flex flex-col gap-4 p-5 ${
              result.iAmPaying
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
            }`}>

              {/* Primary number */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Estimated Monthly
                  </p>
                  <p className="text-4xl font-black text-slate-800 dark:text-slate-100">
                    ${result.amount.toLocaleString()}
                  </p>
                </div>
                <div className={`text-center px-4 py-2.5 rounded-xl flex-shrink-0 ${
                  result.iAmPaying
                    ? 'bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-200'
                    : 'bg-green-100 dark:bg-green-800/40 text-green-800 dark:text-green-200'
                }`}>
                  <p className="text-sm font-black">{result.iAmPaying ? '↑ You pay' : '↓ You receive'}</p>
                  <p className="text-xs mt-0.5">{result.iAmPaying ? 'to co-parent' : 'from co-parent'}</p>
                </div>
              </div>

              {/* Range */}
              <div className="bg-white/60 dark:bg-white/10 rounded-xl px-4 py-2.5">
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-0.5">
                  Likely range (±15% judicial discretion)
                </p>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  ${Math.round(result.amount * 0.85).toLocaleString()} – ${Math.round(result.amount * 1.15).toLocaleString()} / month
                </p>
              </div>

              {/* Calculation breakdown */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  How it's calculated
                </p>
                <div className="bg-white/50 dark:bg-white/5 rounded-xl overflow-hidden">
                  {result.breakdown.map((row, i) => (
                    <div key={i} className={`flex justify-between items-baseline px-3 py-2 ${
                      i < result.breakdown.length - 1 ? 'border-b border-slate-100 dark:border-slate-700/50' : ''
                    }`}>
                      <span className="text-xs text-slate-600 dark:text-slate-300 flex-1 pr-3">{row.label}</span>
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-100 flex-shrink-0">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* State-specific note */}
              {result.note && (
                <div className="bg-white/50 dark:bg-white/5 border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-amber-800 dark:text-amber-300">ℹ️ {result.note}</p>
                </div>
              )}
            </section>

            <div className="disclaimer">
              ⚠️ This is an <strong>estimate only</strong> — not legal advice. Courts may deviate based on special circumstances. Use your state's official Child Support Worksheet for exact figures.
            </div>

            {/* CTAs */}
            <div className="flex gap-3 pb-2">
              <Button variant="ghost" size="md" className="flex-1" onClick={handleCopy}>
                {copied ? '✓ Copied' : '📋 Copy'}
              </Button>
              <Button variant="primary" size="md" className="flex-1" onClick={handleDraft}>
                Draft Agreement →
              </Button>
            </div>
          </>
        ) : (
          <div className="card text-center py-10 flex flex-col items-center gap-3 text-slate-400">
            <span className="text-4xl">💰</span>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Enter your income above to see an estimate
            </p>
            {!form.state && (
              <p className="text-xs text-slate-400">Select your state first to see the applicable formula</p>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
